// websocket/WebsocketClient.ts
import { makeBackoffStrategy } from "./runtime/backOffStrategy";
import { browserClock } from "./runtime/clock";
import { browserSocketFactory } from "./runtime/socketFactory";
import { browserVisibility } from "./runtime/visibility";
import type { AWebsocketState } from "./states/AWebsocketState";
import { ConnectedState } from "./states/ConnectedState";
import { IdleState } from "./states/IdleState";
import {
  type IWsClock,
  type IWsSocket,
  type IWsVisibility,
  type TReconnectConfig,
  type TWebsocketClientOptions,
  type TWebsocketClientParams,
  type TWsBackoffStrategy,
  type TWsSocketFactory,
  WebsocketStateCode,
  WsReadyState,
} from "./types";

const DEFAULT_HEARTBEAT_MS = 100_000;
const DEFAULT_INACTIVITY_MS = 300_000;

export class WebsocketClient {
  // Identity & config
  private readonly userId: string;
  private baseUrl: string;
  private readonly options: TWebsocketClientOptions;
  private readonly queryParams = new Map<string, string>();

  // Runtime
  private websocket: IWsSocket | null = null;
  private state: AWebsocketState = new IdleState(this);
  private shutdownRequested = false;

  // Reconnect
  private reconnectAttempt = 0;
  private readonly reconnect: TReconnectConfig;

  // Watchdogs
  private readonly timings: { heartbeatMs: number; inactivityMs: number };

  // Outbox
  private outbox: string[] = [];
  private readonly outboxMax: number;

  // Services
  private readonly clock: IWsClock;
  private readonly visibility: IWsVisibility;
  private readonly backoff: TWsBackoffStrategy;
  private readonly createSocket: TWsSocketFactory;

  // App-level hooks
  public onOpenHandler?: () => void;
  public onCloseHandler?: () => void;
  public onMessageHandler?: (event: MessageEvent) => void;

  constructor(params: TWebsocketClientParams, options: TWebsocketClientOptions) {
    this.userId = params.userId;
    this.baseUrl = params.url ?? import.meta.env.VITE_WEBSOCKET_URL;
    this.options = options;

    this.reconnect = {
      baseDelayMs: 1000,
      maxDelayMs: 15000,
      jitterRatio: 0.2,
      maxAttempts: Number.POSITIVE_INFINITY,
      ...(options.reconnect ?? {}),
    };

    this.timings = {
      heartbeatMs: options.timings?.heartbeatMs ?? DEFAULT_HEARTBEAT_MS,
      inactivityMs: options.timings?.inactivityMs ?? DEFAULT_INACTIVITY_MS,
    };

    this.outboxMax = options.outboxMax ?? 100;

    // Defaults for services
    this.clock = options.clock ?? browserClock;
    this.visibility = options.visibility ?? browserVisibility;
    this.backoff =
      options.backoff ??
      makeBackoffStrategy({
        baseDelayMs: this.reconnect.baseDelayMs,
        maxDelayMs: this.reconnect.maxDelayMs,
        jitterRatio: this.reconnect.jitterRatio,
      });
    this.createSocket = options.socketFactory ?? browserSocketFactory;
  }

  // Public API
  connect(): void {
    this.state.connect();
  }
  disconnect(): void {
    this.state.disconnect();
  }
  send(message: string): void {
    this.state.send(message);
  }
  sendJson(payload: unknown): void {
    this.state.sendJson(payload);
  }
  isConnected(): boolean {
    return this.state instanceof ConnectedState;
  }

  // App-level hooks
  setOnOpenHandler(handler: () => void): void {
    this.onOpenHandler = handler;
  }
  setOnCloseHandler(handler: () => void): void {
    this.onCloseHandler = handler;
  }
  setOnMessage(handler: (event: MessageEvent) => void): void {
    this.onMessageHandler = handler;
  }
  emitMessage(ev: MessageEvent): void {
    this.options.onMessage?.(ev);
  }

  // Services for states
  getClock(): IWsClock {
    return this.clock;
  }
  getVisibility(): IWsVisibility {
    return this.visibility;
  }
  getBackoff(): TWsBackoffStrategy {
    return this.backoff;
  }

  // State machine
  setState(next: AWebsocketState): void {
    const from = this.state.constructor.name;
    this.state.exit();
    this.state = next;
    this.state.enter();
    this.options.onStateChange?.(from, next.constructor.name);
  }

  // Socket lifecycle
  setWebsocket(ws: IWsSocket): void {
    this.websocket = ws;
    ws.onopen = (ev) => this.state.onOpen(ev);
    ws.onmessage = (ev) => this.state.onMessage(ev);
    ws.onclose = (ev) => this.state.onClose(ev);
    ws.onerror = (ev) => this.state.onError(ev);
  }
  getWebsocket(): IWsSocket | null {
    return this.websocket;
  }

  openFreshSocket(): IWsSocket {
    const prev = this.websocket;
    if (prev && (prev.readyState === WsReadyState.OPEN || prev.readyState === WsReadyState.CONNECTING)) {
      try {
        prev.onopen = prev.onmessage = prev.onerror = prev.onclose = null;
        prev.close(WebsocketStateCode.NORMAL_CLOSURE, "socket replaced");
      } catch {
        console.error("Fail to close websocket");
      }
    }
    const ws = this.createSocket(this.buildUrl());
    this.setWebsocket(ws);
    return ws;
  }

  closeSocket(code = WebsocketStateCode.NORMAL_CLOSURE, reason = "client close"): void {
    const ws = this.websocket;
    if (!ws) {
      return;
    }
    if (ws.readyState === WsReadyState.OPEN || ws.readyState === WsReadyState.CONNECTING) {
      try {
        ws.close(code, reason);
      } catch {
        console.error("Fail to close websocket");
      }
    }
  }

  // Outbox
  enqueueOrSend(message: string): void {
    const ws = this.websocket;
    if (ws && ws.readyState === WsReadyState.OPEN) {
      try {
        ws.send(message);
        return;
      } catch {
        console.error("Fail to send websocket message");
      }
    }
    if (this.outbox.length >= this.outboxMax) {
      this.outbox.shift();
    }
    this.outbox.push(message);
  }
  flushOutbox(): void {
    const ws = this.websocket;
    if (!ws || ws.readyState !== WsReadyState.OPEN) {
      return;
    }
    while (this.outbox.length > 0) {
      const msg = this.outbox[0];
      try {
        ws.send(msg);
        this.outbox.shift();
      } catch {
        break;
      }
    }
  }

  // Reconnect helpers
  incrementReconnectAttempt(): number {
    this.reconnectAttempt += 1;
    return this.reconnectAttempt;
  }
  resetReconnectAttempt(): void {
    this.reconnectAttempt = 0;
  }
  getReconnectConfig(): TReconnectConfig {
    return this.reconnect;
  }

  getHeartbeatMs(): number {
    return this.timings.heartbeatMs;
  }
  getInactivityMs(): number {
    return this.timings.inactivityMs;
  }

  // URL
  setQueryParam(key: string, value: string | null | undefined): void {
    if (value == null) {
      this.queryParams.delete(key);
    } else {
      this.queryParams.set(key, String(value));
    }
  }
  private buildUrl(): string {
    const qs = new URLSearchParams({ user: this.userId });
    for (const [k, v] of this.queryParams) {
      qs.set(k, v);
    }
    return `${this.baseUrl}?${qs}`;
  }

  // Auth guard
  shouldConnect(): boolean {
    return this.options.shouldConnect?.() ?? true;
  }

  // Shutdown intent
  requestShutdown(): void {
    this.shutdownRequested = true;
  }
  clearShutdownRequest(): void {
    this.shutdownRequested = false;
  }
  isShutdownRequested(): boolean {
    return this.shutdownRequested;
  }
}
